import {
  generateUnifiedDocumentSchema
} from "./chunk-ULFG7UHX.js";
import {
  PPTX_JSON_SCHEMA_URLS,
  PptxJsonComponentDefinitionSchema
} from "./chunk-TPEJAO7A.js";
import "./chunk-RLXWATX4.js";
import {
  PptxComponentDefinitionSchema,
  PptxSlideContentSchema,
  PptxStandardComponentDefinitionSchema
} from "./chunk-E6FFR5Y3.js";
import {
  PPTX_STANDARD_COMPONENTS_REGISTRY,
  PositionSchema,
  PresentationPropsSchema,
  ShadowSchema,
  SlideBackgroundSchema,
  SlidePropsSchema,
  TransitionSchema,
  VerticalAlignmentSchema,
  createAllPptxComponentSchemas,
  createPptxComponentSchemaObject,
  getAllPptxComponentNames,
  getPptxComponentsByCategory,
  getPptxContainerComponents,
  getPptxContentComponents,
  getPptxStandardComponent,
  isPptxStandardComponent,
  pptxComponentRequiresProps
} from "./chunk-GDFBPBEM.js";
import {
  BUILT_IN_PPTX_THEME_NAMES,
  ColorValueSchema,
  SEMANTIC_COLOR_ALIASES,
  SEMANTIC_COLOR_NAMES,
  STYLE_NAMES,
  StyleNameSchema,
  TextStyleSchema,
  ThemeConfigSchema,
  isValidThemeConfig
} from "./chunk-VJY4O4VQ.js";
import {
  ChartComponentDefaultsSchema,
  GradientFillSchema,
  GradientStopSchema,
  HighchartsComponentDefaultsSchema,
  ImageComponentDefaultsSchema,
  PATTERN_FILL_PRESETS,
  PatternFillSchema,
  PptxChartPropsSchema,
  PptxComponentDefaultsSchema,
  PptxHighchartsPropsSchema,
  PptxImagePropsSchema,
  PptxTablePropsSchema,
  ShapeComponentDefaultsSchema,
  ShapePropsSchema,
  ShapeTypeSchema,
  TableComponentDefaultsSchema,
  TextComponentDefaultsSchema,
  TextPropsSchema,
  TextRunSchema
} from "./chunk-VLQAI2V6.js";
import {
  PPTX_BASE_SCHEMA_METADATA,
  PPTX_COMPONENT_METADATA,
  convertToJsonSchema
} from "./chunk-AD6NRP7M.js";
import {
  addPptxBlockAuthoringSchemas,
  dispatchPptxRootByRenderer,
  preparePptxDocumentBlockTargets
} from "./chunk-4FGNUZM3.js";
import {
  DEFAULT_PPTX_RENDERER_ID,
  PPTX_RENDERER_IDS,
  collectPptxRendererErrors,
  pptxComponentDefinitionName
} from "./chunk-Y2ODB5VW.js";

// src/validation/image-source-conflicts.ts
var IMAGE_SOURCE_FIELDS = ["path", "base64", "svg"];
function presentImageSources(props) {
  if (!props || typeof props !== "object") return [];
  const p = props;
  return IMAGE_SOURCE_FIELDS.filter((f) => {
    const v = p[f];
    return typeof v === "string" && v.trim().length > 0;
  });
}
function collectImageSourceConflicts(data) {
  const errors = [];
  const visit = (node, path) => {
    if (Array.isArray(node)) {
      node.forEach((item, i) => visit(item, `${path}/${i}`));
      return;
    }
    if (!node || typeof node !== "object") return;
    if (node.name === "image") {
      const present = presentImageSources(node.props);
      if (present.length > 1) {
        errors.push({
          path: `${path}/props`,
          message: `Image component accepts only one source, but found ${present.map((f) => `"${f}"`).join(", ")}. Use exactly one of "path", "base64", or "svg".`,
          code: "mutually_exclusive"
        });
      }
    }
    for (const key of Object.keys(node)) {
      visit(node[key], `${path}/${key}`);
    }
  };
  visit(data, "");
  return errors;
}

// src/validation/text-content-conflicts.ts
function collectTextContentConflicts(data) {
  const errors = [];
  const visit = (node, path, inDefinition) => {
    if (Array.isArray(node)) {
      node.forEach((item, i) => visit(item, `${path}/${i}`, inDefinition));
      return;
    }
    if (!node || typeof node !== "object") return;
    if (!inDefinition && node.name === "text" && node.props && typeof node.props === "object") {
      const hasText = typeof node.props.text === "string";
      const hasRuns = Array.isArray(node.props.runs);
      if (hasText && hasRuns) {
        errors.push({
          path: `${path}/props`,
          message: 'Text component accepts either "text" or "runs", not both. Use exactly one of the two.',
          code: "mutually_exclusive"
        });
      } else if (!hasText && !hasRuns) {
        errors.push({
          path: `${path}/props`,
          message: 'Text component requires content: set either "text" or "runs".',
          code: "required_property"
        });
      }
    }
    for (const key of Object.keys(node)) {
      visit(
        node[key],
        `${path}/${key}`,
        inDefinition || path === "/props" && key === "blocks"
      );
    }
  };
  visit(data, "", false);
  return errors;
}

// src/validation/unified/index.ts
import { Value as Value2 } from "@sinclair/typebox/value";
import {
  explainRemovedBlockSyntax,
  readBlockDefinitions,
  transformValueErrors as transformValueErrors2,
  validateBlockInvocations
} from "@json-to-office/shared";

// src/validation/unified/deep-validator.ts
import { Value } from "@sinclair/typebox/value";
import { transformValueErrors } from "@json-to-office/shared";
var COMPONENT_SCHEMAS = Object.fromEntries(
  PPTX_STANDARD_COMPONENTS_REGISTRY.map((c) => [c.name, c.propsSchema])
);
var ROOT_COMPONENT_NAMES = new Set(
  PPTX_STANDARD_COMPONENTS_REGISTRY.filter(
    (c) => Boolean(c.special?.hasSchemaField)
  ).map((c) => c.name)
);
var COMPONENT_OBJECT_KEYS = /* @__PURE__ */ new Set([
  "name",
  "id",
  "enabled",
  "props",
  "children"
]);
var CUSTOM_COMPONENT_OBJECT_KEYS = /* @__PURE__ */ new Set([
  ...COMPONENT_OBJECT_KEYS,
  "version"
]);
var ROOT_OBJECT_KEYS = /* @__PURE__ */ new Set([...COMPONENT_OBJECT_KEYS, "$schema"]);
ROOT_OBJECT_KEYS.add("renderer");
var SIBLING_KEY_TYPES = {
  id: "string",
  enabled: "boolean",
  $schema: "string",
  version: "string"
};
function collectSiblingKeyErrors(node, path, allowedKeys, label, errors) {
  for (const key of Object.keys(node)) {
    if (!allowedKeys.has(key)) {
      errors.push({
        path: `${path}/${key}`,
        message: `Unknown field "${key}" on ${label}`,
        code: "unknown_field"
      });
      continue;
    }
    const expected = SIBLING_KEY_TYPES[key];
    if (expected && typeof node[key] !== expected) {
      errors.push({
        path: `${path}/${key}`,
        message: `Field "${key}" on ${label} must be a ${expected}`,
        code: "invalid_type"
      });
    }
  }
}
function deepValidatePresentation(data, opts = {}) {
  const allErrors = [];
  if (!data || typeof data !== "object") {
    allErrors.push({
      path: "root",
      message: "Presentation must be an object",
      code: "invalid_type"
    });
    return allErrors;
  }
  if (!data.name) {
    allErrors.push({
      path: "/name",
      message: 'Missing required field "name"',
      code: "required_property"
    });
  } else if (!ROOT_COMPONENT_NAMES.has(data.name)) {
    const expected = [...ROOT_COMPONENT_NAMES].map((n) => `"${n}"`).join(", ");
    allErrors.push({
      path: "/name",
      message: `Invalid name "${data.name}". Expected ${expected}`,
      code: "invalid_value"
    });
  }
  collectSiblingKeyErrors(
    data,
    "",
    ROOT_OBJECT_KEYS,
    "the root component",
    allErrors
  );
  const rootDef = getPptxStandardComponent(data.name);
  if (!("props" in data)) {
    if (!rootDef || pptxComponentRequiresProps(rootDef)) {
      allErrors.push({
        path: "/props",
        message: 'Missing required field "props"',
        code: "required_property"
      });
    }
  } else if (ROOT_COMPONENT_NAMES.has(data.name)) {
    allErrors.push(
      ...validateComponentProps(data.name, data.props, "/props", opts)
    );
  }
  if (!data.children) {
    allErrors.push({
      path: "/children",
      message: 'Missing required field "children"',
      code: "required_property"
    });
  } else if (!Array.isArray(data.children)) {
    allErrors.push({
      path: "/children",
      message: 'Field "children" must be an array',
      code: "invalid_type"
    });
  }
  walkComponentTree(data, "", opts, allErrors);
  return allErrors;
}
function walkComponentTree(node, path, opts, errors) {
  if (!node || typeof node !== "object") return;
  const validateEntry = (child, childPath) => {
    if (!child || typeof child !== "object" || Array.isArray(child)) {
      errors.push({
        path: childPath,
        message: "Component must be an object",
        code: "invalid_type"
      });
      return;
    }
    if (typeof child.name !== "string" || child.name.length === 0) {
      errors.push({
        path: `${childPath}/name`,
        message: 'Component missing required field "name"',
        code: "required_property"
      });
      return;
    }
    const isCustomComponent = opts.knownCustomNames?.has(child.name) ?? false;
    const allowedObjectKeys = isCustomComponent ? CUSTOM_COMPONENT_OBJECT_KEYS : COMPONENT_OBJECT_KEYS;
    collectSiblingKeyErrors(
      child,
      childPath,
      allowedObjectKeys,
      `component "${child.name}"`,
      errors
    );
    if (isCustomComponent) {
      if (!("props" in child)) {
        errors.push({
          path: `${childPath}/props`,
          message: `Component "${child.name}" is missing required field "props"`,
          code: "required_property",
          suggestion: `Add a "props" object holding the content "${child.name}" renders.`
        });
      }
      walkComponentTree(child, childPath, opts, errors);
      return;
    }
    const def = getPptxStandardComponent(child.name);
    const propsPath = `${childPath}/props`;
    if ("props" in child) {
      errors.push(
        ...validateComponentProps(child.name, child.props, propsPath, opts)
      );
    } else if (def && pptxComponentRequiresProps(def)) {
      const declared = def.propsSchema.required ?? [];
      errors.push({
        path: propsPath,
        message: `Component "${child.name}" is missing required field "props"`,
        code: "required_property",
        suggestion: declared.length > 0 ? `Add "props" carrying ${declared.map((f) => `"${f}"`).join(", ")}.` : `Add a "props" object holding the content "${child.name}" renders.`
      });
    } else {
      errors.push(...validateComponentProps(child.name, {}, propsPath, opts));
    }
    if (def && !def.hasChildren && child.children != null) {
      errors.push({
        path: `${childPath}/children`,
        message: `Component "${child.name}" does not accept children`,
        code: "invalid_value"
      });
      return;
    }
    walkComponentTree(child, childPath, opts, errors);
  };
  const parentDef = getPptxStandardComponent(node.name);
  const checkAllowedChild = (child, childPath) => {
    if (parentDef?.allowedChildren && child && typeof child === "object" && typeof child.name === "string" && getPptxStandardComponent(child.name) && !parentDef.allowedChildren.includes(child.name)) {
      const expected = parentDef.allowedChildren.map((n) => `"${n}"`).join(", ");
      errors.push({
        path: `${childPath}/name`,
        message: `Component "${child.name}" is not allowed inside "${node.name}". Expected ${expected}`,
        code: "invalid_value"
      });
    }
  };
  if (Array.isArray(node.children)) {
    node.children.forEach((child, i) => {
      const childPath = `${path}/children/${i}`;
      checkAllowedChild(child, childPath);
      validateEntry(child, childPath);
    });
  } else if (node.children != null && path !== "") {
    errors.push({
      path: `${path}/children`,
      message: 'Field "children" must be an array',
      code: "invalid_type"
    });
  }
}
function validateComponentProps(componentName, props, basePath, opts = {}) {
  const errors = [];
  const schema = COMPONENT_SCHEMAS[componentName];
  if (!schema) {
    errors.push({
      path: basePath.replace(/\/props$/, "/name"),
      message: `Unknown component "${componentName}"`,
      code: "unknown_component"
    });
    return errors;
  }
  let toCheck = props;
  if (opts.allowUnknownFields) {
    toCheck = Value.Clean(schema, Value.Clone(toCheck));
  }
  if (!Value.Check(schema, toCheck)) {
    const valueErrors = [...Value.Errors(schema, toCheck)];
    const transformedErrors = transformValueErrors(valueErrors, {
      maxErrors: 100
    });
    transformedErrors.forEach((error) => {
      const fullPath = error.path === "root" ? basePath : `${basePath}${error.path.startsWith("/") ? error.path : "/" + error.path}`;
      errors.push({
        ...error,
        path: fullPath
      });
    });
  }
  return errors;
}
function comprehensiveValidatePresentation(data, existingErrors = [], opts = {}) {
  const deepErrors = deepValidatePresentation(data, opts);
  const filteredExisting = existingErrors.filter(
    (e) => !isGenericUnionCatchAll(e)
  );
  return deduplicateErrors([...filteredExisting, ...deepErrors]);
}
function isGenericUnionCatchAll(error) {
  const atRoot = !error.path || error.path === "root" || error.path === "/";
  if (!atRoot) return false;
  const msg = error.message || "";
  return /invalid component configurations?/i.test(msg) || /invalid document structure/i.test(msg);
}
function deduplicateErrors(errors) {
  const seen = /* @__PURE__ */ new Set();
  const unique = [];
  for (const error of errors) {
    const key = `${error.path}:${error.message}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(error);
    }
  }
  return unique;
}

// src/validation/unified/index.ts
function parseJsonInput(jsonInput) {
  if (typeof jsonInput !== "string") return { parsed: jsonInput };
  try {
    return { parsed: JSON.parse(jsonInput) };
  } catch (err) {
    return {
      error: {
        path: "root",
        message: `Invalid JSON: ${err?.message ?? String(err)}`,
        code: "json_parse_error"
      }
    };
  }
}
function validatePresentationDocument(data, opts = {}) {
  const errors = comprehensiveValidatePresentation(data, [], opts);
  errors.push(...collectImageSourceConflicts(data));
  errors.push(...collectTextContentConflicts(data));
  errors.push(...collectPptxRendererErrors(data));
  errors.push(
    ...validateBlockInvocations(data, readBlockDefinitions(data), "pptx", [
      ...opts.knownCustomNames ?? []
    ])
  );
  const valid = errors.length === 0;
  return {
    valid,
    errors: explainRemovedBlockSyntax(data, "pptx", errors),
    documentType: "pptx",
    data: valid ? data : void 0
  };
}
function validateJsonPresentationDocument(jsonInput, opts = {}) {
  const { parsed, error } = parseJsonInput(jsonInput);
  if (error) return { valid: false, errors: [error], documentType: "pptx" };
  return validatePresentationDocument(parsed, opts);
}
function validatePptxTheme(data) {
  if (Value2.Check(ThemeConfigSchema, data)) {
    return { valid: true, errors: [], data };
  }
  const valueErrors = [...Value2.Errors(ThemeConfigSchema, data)];
  const errors = transformValueErrors2(valueErrors, { maxErrors: 100 });
  return { valid: false, errors };
}
function validateJsonPptxTheme(jsonInput) {
  const { parsed, error } = parseJsonInput(jsonInput);
  if (error) return { valid: false, errors: [error] };
  return validatePptxTheme(parsed);
}
var validate = {
  document: (data) => validatePresentationDocument(data),
  jsonDocument: (jsonInput) => validateJsonPresentationDocument(jsonInput),
  theme: (data) => validatePptxTheme(data),
  jsonTheme: (jsonInput) => validateJsonPptxTheme(jsonInput),
  isDocument: (data) => validatePresentationDocument(data).valid,
  isTheme: (data) => validatePptxTheme(data).valid
};
var validateStrict = {
  document: (data) => validatePresentationDocument(data),
  jsonDocument: (jsonInput) => validateJsonPresentationDocument(jsonInput),
  theme: (data) => validatePptxTheme(data),
  jsonTheme: (jsonInput) => validateJsonPptxTheme(jsonInput)
};

// src/index.ts
import {
  transformValueError,
  transformValueErrors as transformValueErrors3,
  DEFAULT_ERROR_CONFIG,
  createErrorConfig
} from "@json-to-office/shared";
import {
  latestVersion,
  isValidSemver,
  parseSemver,
  compareSemver
} from "@json-to-office/shared";
import {
  fixSchemaReferences,
  createComponentSchema,
  exportSchemaToFile
} from "@json-to-office/shared";
var PPTX_SHARED_VERSION = "1.0.0";
export {
  BUILT_IN_PPTX_THEME_NAMES,
  ChartComponentDefaultsSchema,
  ColorValueSchema,
  DEFAULT_ERROR_CONFIG,
  DEFAULT_PPTX_RENDERER_ID,
  GradientFillSchema,
  GradientStopSchema,
  HighchartsComponentDefaultsSchema,
  ImageComponentDefaultsSchema,
  PATTERN_FILL_PRESETS,
  PPTX_BASE_SCHEMA_METADATA,
  PPTX_COMPONENT_METADATA,
  PPTX_JSON_SCHEMA_URLS,
  PPTX_RENDERER_IDS,
  PPTX_SHARED_VERSION,
  PPTX_STANDARD_COMPONENTS_REGISTRY,
  PatternFillSchema,
  PositionSchema,
  PptxChartPropsSchema,
  PptxComponentDefaultsSchema,
  PptxComponentDefinitionSchema,
  PptxHighchartsPropsSchema,
  PptxImagePropsSchema,
  PptxJsonComponentDefinitionSchema,
  PptxSlideContentSchema,
  PptxStandardComponentDefinitionSchema,
  PptxTablePropsSchema,
  PresentationPropsSchema,
  SEMANTIC_COLOR_ALIASES,
  SEMANTIC_COLOR_NAMES,
  STYLE_NAMES,
  ShadowSchema,
  ShapeComponentDefaultsSchema,
  ShapePropsSchema,
  ShapeTypeSchema,
  SlideBackgroundSchema,
  SlidePropsSchema,
  StyleNameSchema,
  TableComponentDefaultsSchema,
  TextComponentDefaultsSchema,
  TextPropsSchema,
  TextRunSchema,
  TextStyleSchema,
  ThemeConfigSchema,
  TransitionSchema,
  VerticalAlignmentSchema,
  addPptxBlockAuthoringSchemas,
  collectImageSourceConflicts,
  collectPptxRendererErrors,
  collectTextContentConflicts,
  compareSemver,
  comprehensiveValidatePresentation,
  convertToJsonSchema,
  createAllPptxComponentSchemas,
  createComponentSchema,
  createErrorConfig,
  createPptxComponentSchemaObject,
  deepValidatePresentation,
  dispatchPptxRootByRenderer,
  exportSchemaToFile,
  fixSchemaReferences,
  generateUnifiedDocumentSchema,
  getAllPptxComponentNames,
  getPptxComponentsByCategory,
  getPptxContainerComponents,
  getPptxContentComponents,
  getPptxStandardComponent,
  isPptxStandardComponent,
  isValidSemver,
  isValidThemeConfig,
  latestVersion,
  parseSemver,
  pptxComponentDefinitionName,
  pptxComponentRequiresProps,
  preparePptxDocumentBlockTargets,
  presentImageSources,
  transformValueError,
  transformValueErrors3 as transformValueErrors,
  validate,
  validateJsonPptxTheme,
  validateJsonPresentationDocument,
  validatePptxTheme,
  validatePresentationDocument,
  validateStrict
};
//# sourceMappingURL=index.js.map